import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { InvoiceData, InvoiceItem } from "../types/InvoiceData";
import InvoiceUtils, { BILL_TO_DEFAULT_DATA, DEFAULT_CURRENCY, DEFAULT_ITEM_QUANTITY, parseInvoiceIssueDate } from "../utils/InvoiceUtils";
import { STORAGE_KEYS } from "./useLocalStorage";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { Country } from "../utils/Countries";
import { fetchOpsUserByCoreId } from "./useUserInfo";
import WorkLogService from "../services/WorkLogService";
import ApiError from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiError";
import { QueryKeyFactory } from "../services/queryKeys";
import { unwrap } from "../utils/queryFn";

const workLogKeys = new QueryKeyFactory(WorkLogService.path);

const increaseLastInvoiceNumber = (): string => {
  const lastInvoiceNumber = localStorage.getItem(STORAGE_KEYS.LAST_INVOICE_NUMBER);
  return lastInvoiceNumber ? String(Number(lastInvoiceNumber) + 1) : "1";
};

const buildEmptyInvoice = (): InvoiceData => {
  const now = new Date();

  return {
    invoiceNumber: increaseLastInvoiceNumber(),
    issueDate: InvoiceUtils.getLastBusinessDayOfMonth(now.getFullYear(), now.getMonth()),
    currency: DEFAULT_CURRENCY,
    from: {
      beneficiaryName: "",
      country: "",
      addressLine1: "",
      addressLine2: "",
    },
    billTo: "",
    items: [{ description: "", quantity: DEFAULT_ITEM_QUANTITY, unitPrice: 0, totalAmount: 0 }],
    paymentTerms: "",
  };
};

const loadInitialData = (): InvoiceData => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.INVOICE_DRAFT);
    if (raw) {
      const parsedData = JSON.parse(raw) as InvoiceData;
      parsedData.invoiceNumber = increaseLastInvoiceNumber();

      return parsedData;
    }
  } catch {
    console.warn("parsing error on INVOICE_DRAFT");
  }
  return buildEmptyInvoice();
};

const useInvoice = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<InvoiceData>(loadInitialData);
  const [isPrefilling, setIsPrefilling] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.INVOICE_DRAFT, JSON.stringify(formData));
  }, [formData]);

  const prefillForUser = async () => {
    setIsPrefilling(true);
    try {
      const issueDate = parseInvoiceIssueDate(formData.issueDate);
      if (!issueDate) {
        NotificationService.warn("Please set a correct issue date");
        return;
      }
      const year = issueDate.getFullYear();
      const month = issueDate.getMonth();

      const coreUserId = AuthService.getUser().externalId;
      const opsUser = await fetchOpsUserByCoreId(queryClient, coreUserId);
      // Default staleTime (0): the hours must be live every time the user presses "prefill".
      const internalHours = await queryClient.fetchQuery({
        queryKey: workLogKeys.detail(`internal-hours:${year}-${month}`, { coreUserId }),
        queryFn: () => unwrap(WorkLogService.getTotalInternalHours(year, month)),
      });

      setFormData((prev) => {
        const unitPrice = prev.items[0]?.unitPrice ?? 0;
        const totalAmount = internalHours * unitPrice;
        return {
          ...prev,
          from: {
            ...prev.from,
            beneficiaryName: `${opsUser.firstName} ${opsUser.lastName}`,
            country: Country[opsUser.country as keyof typeof Country] ?? "",
          },
          items: [
            { description: "Software Engineering Services", quantity: internalHours, unitPrice: unitPrice, totalAmount: totalAmount },
          ],
        };
      });
    } catch (error) {
      // ApiError was already toasted by the QueryCache onError; only toast unexpected failures.
      if (!(error instanceof ApiError)) {
        NotificationService.error("Failed to prefill invoice with user data");
      }
    } finally {
      setIsPrefilling(false);
      updateField("billTo", BILL_TO_DEFAULT_DATA);
    }
  };

  const addItem = () => {
    const newItem: InvoiceItem = {
      description: "",
      quantity: DEFAULT_ITEM_QUANTITY,
      unitPrice: 0,
      totalAmount: 0,
    };
    setFormData((prev) => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const removeItem = (index: number) => {
    setFormData((prev) => {
      if (prev.items.length <= 1) {
        return prev;
      }
      return { ...prev, items: prev.items.filter((_, i) => i !== index) };
    });
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const updateField = <K extends keyof InvoiceData>(field: K, value: InvoiceData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    localStorage.removeItem(STORAGE_KEYS.INVOICE_DRAFT);
    setFormData(buildEmptyInvoice());
  };

  return {
    formData,
    isPrefilling,
    prefillForUser,
    addItem,
    removeItem,
    updateItem,
    updateField,
    resetForm,
  };
};

export default useInvoice;
export type UseInvoiceReturnType = ReturnType<typeof useInvoice>;
